import useCapacitor from "@/hooks/useCapacitor";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import { Fragment } from "react";

function DialogCloseButton({
  onClick,
  className,
}: {
  readonly onClick: () => void;
  readonly className?: string;
}) {
  return (
    <button
      type="button"
      title="Close panel"
      aria-label="Close panel"
      className={clsx(
        "-tw-mr-2 -tw-mt-2.5 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-p-2.5 tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-white/[0.04] hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-white/20",
        className
      )}
      onClick={onClick}
    >
      <svg
        className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-text-current"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M18 6L6 18M6 6L18 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function DialogHeader({
  title,
  showDesktopCloseButton,
  onClose,
  className,
}: {
  readonly title: string | undefined;
  readonly showDesktopCloseButton: boolean;
  readonly onClose: () => void;
  readonly className?: string | undefined;
}) {
  return (
    <div className={clsx("tw-px-4 sm:tw-px-6", className)}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        {title && (
          <DialogTitle className="tw-text-base tw-font-semibold tw-text-iron-50">
            {title}
          </DialogTitle>
        )}
        {showDesktopCloseButton && (
          <DialogCloseButton
            onClick={onClose}
            className="tw-hidden md:tw-inline-flex"
          />
        )}
      </div>
    </div>
  );
}

function getSlideTransition(tabletModal?: boolean) {
  return {
    enter:
      "tw-duration-250 sm:tw-duration-350 tw-transform tw-transition tw-ease-in-out",
    enterFrom: clsx(
      "tw-translate-y-full",
      tabletModal && "md:tw-translate-y-4 md:tw-opacity-0"
    ),
    enterTo: clsx("tw-translate-y-0", tabletModal && "md:tw-opacity-100"),
    leave:
      "tw-duration-250 sm:tw-duration-350 tw-transform tw-transition tw-ease-in-out",
    leaveFrom: clsx("tw-translate-y-0", tabletModal && "md:tw-opacity-100"),
    leaveTo: clsx(
      "tw-translate-y-full",
      tabletModal && "md:tw-translate-y-4 md:tw-opacity-0"
    ),
  };
}

function getBottomPadding(noPadding?: boolean): string {
  return noPadding
    ? "env(safe-area-inset-bottom,0px)"
    : "calc(env(safe-area-inset-bottom,0px) + 1.5rem)";
}

function getDialogHeight({
  tall,
  isCapacitor,
}: {
  readonly tall?: boolean | undefined;
  readonly isCapacitor: boolean;
}): string {
  const viewportHeight = "min(100vh, 100svh)";

  if (tall && !isCapacitor) {
    return `calc(${viewportHeight} - 4rem)`;
  }

  return `calc(${viewportHeight} - 10rem)`;
}

export default function MobileWrapperDialog({
  title,
  isOpen,
  onClose,
  onBeforeLeave,
  onAfterLeave,
  children,
  noPadding,
  tall,
  fixedHeight,
  tabletModal,
  showScrollbar,
  allowOverflow,
  maxWidthClass,
  zIndexClassName = "tw-z-[1010]",
  headerClassName,
  mobileCloseButtonClassName,
  dismissible = true,
}: {
  readonly title?: string | undefined;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onBeforeLeave?: (() => void) | undefined;
  readonly onAfterLeave?: (() => void) | undefined;
  readonly children: React.ReactNode;
  readonly noPadding?: boolean | undefined;
  readonly tall?: boolean | undefined;
  readonly fixedHeight?: boolean | undefined;
  readonly tabletModal?: boolean | undefined;
  readonly showScrollbar?: boolean | undefined;
  readonly allowOverflow?: boolean | undefined;
  readonly maxWidthClass?: string | undefined;
  readonly zIndexClassName?: string | undefined;
  readonly headerClassName?: string | undefined;
  readonly mobileCloseButtonClassName?: string | undefined;
  readonly dismissible?: boolean | undefined;
}) {
  const { isCapacitor, isIos } = useCapacitor();
  const handleClose = () => {
    if (dismissible) {
      onClose();
    }
  };

  const bottomPadding = getBottomPadding(noPadding);
  const dialogHeight = getDialogHeight({
    tall,
    isCapacitor,
  });

  const panelClassNames = clsx(
    "mobile-wrapper-dialog tw-pointer-events-auto tw-relative tw-w-screen",
    !tabletModal && "md:tw-max-w-screen-md",
    !isIos && "tw-transform-gpu tw-will-change-transform",
    tabletModal && ["md:tw-w-full", maxWidthClass ?? "md:tw-max-w-md"]
  );

  const containerClassNames = clsx(
    "tw-pointer-events-none tw-fixed tw-inset-x-0 tw-bottom-0 tw-flex tw-max-w-full tw-justify-center tw-pt-10",
    tabletModal && "md:tw-inset-0 md:tw-items-center md:tw-p-6 md:tw-pt-0"
  );

  const slideTransition = getSlideTransition(tabletModal);

  const showDesktopHeaderCloseButton = dismissible && !!tabletModal;

  return (
    <Transition appear={true} show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={clsx("tailwind-scope tw-absolute", zIndexClassName)}
        onClose={handleClose}
      >
        <TransitionChild
          as={Fragment}
          enter="tw-duration-250 tw-ease-in-out"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-duration-250 tw-ease-in-out"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
          {...(onBeforeLeave ? { beforeLeave: onBeforeLeave } : {})}
          {...(onAfterLeave ? { afterLeave: onAfterLeave } : {})}
        >
          <div className="tw-fixed tw-inset-0 tw-bg-iron-600/60" />
        </TransitionChild>

        <div
          className="tw-fixed tw-inset-0"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          <div
            className="tw-absolute tw-inset-0 tw-overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={containerClassNames}>
              <TransitionChild as={Fragment} {...slideTransition}>
                <DialogPanel
                  className={panelClassNames}
                  style={{ touchAction: "manipulation" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {dismissible && (
                    <TransitionChild
                      as={Fragment}
                      enter="tw-duration-250 tw-ease-in-out"
                      enterFrom="tw-opacity-0"
                      enterTo="tw-opacity-100"
                      leave="tw-duration-250 tw-ease-in-out"
                      leaveFrom="tw-opacity-100"
                      leaveTo="tw-opacity-0"
                    >
                      <div
                        className={clsx(
                          "tw-absolute -tw-top-16 tw-right-0 tw-flex tw-pr-2 tw-pt-4 md:tw-pr-0",
                          tabletModal && "md:tw-hidden"
                        )}
                      >
                        <DialogCloseButton
                          onClick={handleClose}
                          {...(mobileCloseButtonClassName
                            ? { className: mobileCloseButtonClassName }
                            : {})}
                        />
                      </div>
                    </TransitionChild>
                  )}
                  <div
                    className={clsx(
                      "tw-flex tw-flex-col tw-rounded-t-xl tw-bg-iron-950",
                      allowOverflow
                        ? "tw-overflow-visible"
                        : "tw-overflow-hidden",
                      tabletModal && "md:tw-rounded-xl"
                    )}
                    style={{
                      ...(fixedHeight
                        ? { height: dialogHeight }
                        : { maxHeight: dialogHeight }),
                    }}
                  >
                    <div
                      className={clsx(
                        "tw-flex tw-min-h-0 tw-flex-1 tw-scroll-py-3 tw-flex-col",
                        allowOverflow
                          ? "tw-overflow-visible"
                          : "tw-overflow-y-auto",
                        noPadding ? "tw-py-0" : "tw-py-6",
                        showScrollbar &&
                          !allowOverflow &&
                          "tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300"
                      )}
                      style={{ paddingBottom: bottomPadding }}
                    >
                      <DialogHeader
                        title={title}
                        showDesktopCloseButton={showDesktopHeaderCloseButton}
                        onClose={handleClose}
                        className={headerClassName}
                      />
                      {children}
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
